"use client"

import React, { useEffect, useState, Suspense } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { useForm, FormProvider } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import { Button } from "@/shared/components/ui/button"
import axios from "axios"
import { setAuthCookie,registerUser, VerifyOtp, verifySession } from "@/features/auth/api/auth.api"
import { getPostLoginRedirectRoute } from "../../../../../shared/utils/auth-redirect"
import { getProfile, getVolunteerProfile } from "@/features/profile/api/profile.api"
import { UserRole } from "@/shared/constants/user-roles"
// Schemas
const requestOtpSchema = z.object({
  email: z.string().email(),
})

const verifyOtpSchema = z.object({
  email: z.string().email(),
  code: z.string().min(4, "OTP must be at least 4 digits"),
})

type VerifyOtpType = z.infer<typeof verifyOtpSchema>

function LoginByOtpFormContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const emailFromUrl = searchParams.get("email") || ""

  const [otpSent, setOtpSent] = useState(false)
  const [loading, setLoading] = useState(false)

  const formMethods = useForm<VerifyOtpType>({
    resolver: zodResolver(verifyOtpSchema),
    defaultValues: { email: emailFromUrl },
  })

  const { control, setValue, handleSubmit } = formMethods

  // Prefill email
  useEffect(() => {
    if (emailFromUrl) {
      setValue("email", emailFromUrl)
    }
  }, [emailFromUrl, setValue])

  // Step 1: Get OTP
  const handleGetOtp = async () => {
    try {
      setLoading(true)
     const data = await registerUser(emailFromUrl)
     if(data?.success){
        setOtpSent(true)
     }
    } catch (err) {
      console.error("Error sending OTP", err)
    } finally {
      setLoading(false)
    }
  }

  // Step 2: Verify OTP
  const onSubmit = async (data: VerifyOtpType) => {
    try {
      setLoading(true)
      const datas = await VerifyOtp(data.email,data.code)
      if(datas?.success){
        // Get user session to determine redirect route based on role
        const session = await verifySession()
        let currentStep: number | undefined
        if (session?.sub) {
          try {
            const isHomeowner = session.role === UserRole.HOMEOWNER
            const profileRes = isHomeowner
              ? await getProfile()
              : await getVolunteerProfile(Number(session.sub))
            if (profileRes.success && profileRes.data) {
              currentStep = profileRes.data.currentStep
            }
          } catch {
            /* ignore */
          }
        }
        const redirectRoute = session?.role
          ? getPostLoginRedirectRoute(session.role, null, currentStep)
          : "/home"
        if (redirectRoute === "/home") {
          router.push(redirectRoute)
          window.location.href = redirectRoute
        } else {
          router.push(redirectRoute)
        }
      }
    } catch (err) {
      console.error("Invalid OTP", err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <FormProvider {...formMethods}>
      <div className="space-y-5 w-full">
        {/* Email (disabled) */}
        <FormTextInput
          inputGroupProps={{ label: "Email Address" }}
          type="email"
          name="email"
          placeholder="Email"
          control={control}
          disabled
        />

        {!otpSent ? (
          <Button
            type="button"
            onClick={handleGetOtp}
            className="w-full"
            disabled={loading}
          >
            {loading ? "Sending..." : "Get OTP"}
          </Button>
        ) : (
          <>
            {/* OTP Input */}
            <FormTextInput
              inputGroupProps={{ label: "Enter OTP" }}
              type="text"
              name="code"
              placeholder="Enter OTP"
              control={control}
            />
            <Button
              type="button"
              className="w-full"
              disabled={loading}
              onClick={handleSubmit(onSubmit)}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </Button>
          </>
        )}
      </div>
    </FormProvider>
  )
}

function LoginByOtpForm() {
  return (
    <Suspense fallback={
      <div className="space-y-5 w-full">
        <div className="space-y-2">
          <label className="text-sm font-medium">Email Address</label>
          <input
            type="email"
            placeholder="Email"
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
          />
        </div>
        <Button
          type="button"
          className="w-full"
          disabled
        >
          Loading...
        </Button>
      </div>
    }>
      <LoginByOtpFormContent />
    </Suspense>
  )
}

export default LoginByOtpForm
